import {
  Box,
  Button,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  FieldDataTypeConfig,
  InputObjectContentNode,
  InputValueContentNode,
  ListContentNode,
} from '@helo/cms'
import { Assert, Debug, If, flow, pipe, tap } from '@helo/utils'
import { useEffect, useMemo, useRef, useState } from 'react'

import { ClickOutsideBoundary } from '../../components/ClickOutsideBoundary'
import { ChevronIcon } from '../../components/Icons/ChevronIcon'
import { SortEditor } from '../../components/SortEditor'
import { Tooltip } from '../../components/Tooltip/Tooltip'

export type SortEditorPopover = {
  orderByInputValueContentNode: InputValueContentNode
  onChange?: (contentNode: InputValueContentNode) => void
}

export const SortEditorPopover = ({
  orderByInputValueContentNode: orderByInputValueContentNodeProp,
  onChange,
}: SortEditorPopover) => {
  if (!orderByInputValueContentNodeProp) {
    throw new Error('orderByInputValueContentNodeProp is required')
  }
  // Track the internal state of the SortEditor for popover interaction
  const [orderByInputValueContentNode, setOrderByInputValueContentNode] =
    useState<InputValueContentNode>(orderByInputValueContentNodeProp)
  // Update internal state when prop changes
  useEffect(() => {
    setOrderByInputValueContentNode(orderByInputValueContentNodeProp)
  }, [orderByInputValueContentNodeProp])

  const popoverContentRef = useRef<HTMLDivElement>(null)

  return (
    <Popover placement="bottom-end" closeOnBlur={false}>
      {({ onClose, isOpen }) => {
        const handleCancel = () => {
          setOrderByInputValueContentNode(orderByInputValueContentNodeProp)
          onClose()
        }

        const handleApply = () => {
          onChange?.(orderByInputValueContentNode)
          onClose()
        }

        const sortCount = pipe(
          orderByInputValueContentNode,
          ContentNodeUtils.getOfType(),
          Assert.orThrow(
            ContentNodeUtils.isKind('LIST'),
            () =>
              new Error(
                'orderByInputValueContentNode.ofType must be a ListContentNode',
              ),
          ),
          ContentNodeUtils.getItems(),
          items => items?.length ?? 0,
        )

        const firstSortItem = pipe(
          orderByInputValueContentNode,
          ContentNodeUtils.getOfType(),
          Assert.orThrow(
            ContentNodeUtils.isKind('LIST'),
            () =>
              new Error(
                'orderByInputValueContentNode.ofType must be a ListContentNode',
              ),
          ),
          ContentNodeUtils.getItems(),
          items => items?.[0],
          Assert.orReturn(
            ContentNodeUtils.isKind('INPUT_OBJECT'),
            () => undefined,
          ),
        )

        const firstSortItemLabel = pipe(
          firstSortItem,
          Assert.orReturn(
            ContentNodeUtils.isKind('INPUT_OBJECT'),
            () => undefined,
          ),
          ContentNodeUtils.getFields(),
          fields => fields?.[0]?.state?.label as string | undefined,
        )

        // Find the first non-disabled sort field by getting the INPUT_OBJECT config
        // and filtering its fields by sortDisabled
        const firstDefaultItem = useMemo(() => {
          return pipe(
            orderByInputValueContentNode,
            ContentNodeUtils.getDataTypeConfig(),
            DataTypeConfigUtils.getOfType(),
            Assert.orThrow(
              DataTypeConfigUtils.isKind('LIST'),
              () =>
                new Error(
                  'orderByInputValueContentNode.ofType must be a ListDataTypeConfig',
                ),
            ),
            DataTypeConfigUtils.getOfType(),
            Assert.orThrow(
              DataTypeConfigUtils.isKind('INPUT_OBJECT'),
              () =>
                new Error(
                  'orderByInputValueContentNode.ofType must be a InputObjectDataTypeConfig',
                ),
            ),
            DataTypeConfigUtils.getFields(),
            fields => fields?.find(field => field.state?.sortDisabled !== true),
          )
        }, [orderByInputValueContentNode])

        const sortButtonLabelText =
          sortCount === 1 ? firstSortItemLabel : `${sortCount} columns`

        useEffect(() => {
          pipe(
            orderByInputValueContentNode,
            If.then(
              () => !isOpen || sortCount > 0 || !firstDefaultItem?.key,
              () => pipe.return(undefined),
            ),
            ContentNodeUtils.updateContentNode([
              {
                [firstDefaultItem!.key]: 'ASC',
              },
            ]),
            tap(setOrderByInputValueContentNode),
          )
        }, [isOpen])

        const handleClear = () =>
          pipe(
            orderByInputValueContentNodeProp,
            ContentNodeUtils.updateContentNode([]),
            tap(emptyInputValueContentNode => {
              setOrderByInputValueContentNode(emptyInputValueContentNode)
            }),
            tap(emptyInputValueContentNode =>
              onChange?.(emptyInputValueContentNode),
            ),
            tap(() => onClose()),
          )

        return (
          <>
            <PopoverTrigger>
              <Box data-sort-editor-trigger>
                <Tooltip label="Multi Column Sorting" placement="top">
                  {/**  Box seems like an unnecessary element, but it's necessary for Tooltip and Popover combination.
                   * @see https://github.com/chakra-ui/chakra-ui/issues/2843
                   * */}
                  <Button variant="controlButton" size="sm" gap="4px">
                    <Text fontSize="inherit">Sorted By:</Text>
                    <Text fontWeight="700" fontSize="inherit">
                      {sortButtonLabelText}
                    </Text>
                    <ChevronIcon />
                  </Button>
                </Tooltip>
              </Box>
            </PopoverTrigger>
            <PopoverContent
              minWidth="439px"
              pt="8px"
              overflow="hidden"
              borderRadius="4px"
              boxShadow="0 3px 10px 0 rgba(0, 0, 0, 0.05), 0 4px 3px 0 rgba(0, 0, 0, 0.03)"
            >
              <ClickOutsideBoundary
                ref={popoverContentRef}
                boundaryId="sort-editor-popover"
                onClickOutside={handleCancel}
                enabled={isOpen}
              >
                <PopoverBody px="0">
                  {isOpen && orderByInputValueContentNode && (
                    <SortEditor
                      orderByInputValueContentNode={
                        orderByInputValueContentNode
                      }
                      onChange={updatedOrderByInputValueContentNode => {
                        setOrderByInputValueContentNode(
                          updatedOrderByInputValueContentNode,
                        )
                      }}
                    />
                  )}
                </PopoverBody>
                <PopoverFooter padding="16px">
                  <HStack gap="8px" justifyContent="space-between" width="100%">
                    <Button size="sm" variant="tertiary" onClick={handleClear}>
                      Clear
                    </Button>
                    <HStack gap="8px">
                      <Button
                        size="sm"
                        variant="tertiary"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                      <Button size="sm" variant="primary" onClick={handleApply}>
                        Sort
                      </Button>
                    </HStack>
                  </HStack>
                </PopoverFooter>
              </ClickOutsideBoundary>
            </PopoverContent>
          </>
        )
      }}
    </Popover>
  )
}
